
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, Plus, Trash2, Edit2, FileVideo, FileText, Image, Check, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { uploadFile, getFiles, deleteFile, FileMetadata } from "@/services/fileService";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Lesson {
  id: number;
  title: string;
  duration: string;
  completed: boolean;
  videoUrl: string;
  thumbnail: string;
  fileId?: string; // For linking to the actual file in storage
}

interface Course {
  id: number;
  title: string;
  progress: number;
  image: string;
  instructor: string;
  instructorImage: string;
  description: string;
  lessons: Lesson[];
}

// This would be a real component used in the AdminDashboard page
export const TutorialManager = () => {
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddCourseDialog, setShowAddCourseDialog] = useState(false);
  const [showAddLessonDialog, setShowAddLessonDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileMetadata | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<string>("1");
  
  // Form states
  const [newCourse, setNewCourse] = useState({
    title: "",
    instructor: "Hoss Immo Consulting GmbH",
    description: ""
  });
  
  const [newLesson, setNewLesson] = useState({
    title: "",
    duration: "",
    courseId: "1"
  });

  // Fetch tutorial files on component mount
  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    setLoading(true);
    try {
      // Fetch all files - in a real app, we would filter by tutorial-related files
      const allFiles = await getFiles();
      // Filter video files only
      const videoFiles = allFiles.filter(file => file.type.startsWith('video/'));
      setFiles(videoFiles);
    } catch (error) {
      console.error("Error loading files:", error);
      toast.error("Fehler beim Laden der Tutorial-Dateien");
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleUpload(selectedFiles[0]);
    }
  };

  const handleUpload = async (file: File) => {
    // Simulate upload progress
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev === null) return null;
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 300);

    try {
      // Upload the file to tutorials folder
      const uploadedFile = await uploadFile(file, 'tutorials', {
        type: 'tutorial',
        uploadedBy: 'admin'
      });
      
      if (uploadedFile) {
        setUploadProgress(100);
        setTimeout(() => {
          setUploadProgress(null);
          toast.success("Video erfolgreich hochgeladen");
          // Refresh file list
          loadFiles();
        }, 500);
      } else {
        throw new Error("File upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Fehler beim Hochladen des Videos");
      setUploadProgress(null);
    } finally {
      clearInterval(interval);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (confirm("Möchten Sie dieses Video wirklich löschen?")) {
      try {
        await deleteFile(fileId);
        toast.success("Video erfolgreich gelöscht");
        // Refresh file list
        loadFiles();
      } catch (error) {
        console.error("Error deleting file:", error);
        toast.error("Fehler beim Löschen des Videos");
      }
    }
  };

  const handleAddCourse = () => {
    // In a real app, this would add the course to the database
    toast.success("Neuer Kurs hinzugefügt");
    setNewCourse({
      title: "",
      instructor: "Hoss Immo Consulting GmbH",
      description: ""
    });
    setShowAddCourseDialog(false);
  };

  const handleAddLesson = () => {
    // In a real app, this would add the lesson to the database and link the file
    if (selectedFile) {
      toast.success("Neue Lektion hinzugefügt");
      setNewLesson({
        title: "",
        duration: "",
        courseId: "1"
      });
      setSelectedFile(null);
      setShowAddLessonDialog(false);
    } else {
      toast.error("Bitte wählen Sie eine Video-Datei aus");
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="glass-morphism rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileVideo size={20} />
          Tutorial-Verwaltung
        </h2>
        
        <Tabs defaultValue="videos">
          <TabsList className="mb-4">
            <TabsTrigger value="videos">Video-Dateien</TabsTrigger>
            <TabsTrigger value="courses">Kurse</TabsTrigger>
            <TabsTrigger value="lessons">Lektionen</TabsTrigger>
          </TabsList>
          
          <TabsContent value="videos">
            <div className="border border-gray-800 rounded-lg p-4 mb-6">
              <h3 className="text-md font-medium mb-3 flex items-center gap-2">
                <FileVideo size={18} />
                Tutorial-Videos hochladen
              </h3>
              
              <div 
                className="bg-black/20 border-2 border-dashed border-gray-700 rounded-lg p-8 flex flex-col items-center justify-center hover:border-[#41c7f0]/50 transition-colors cursor-pointer"
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <Upload size={32} className="text-gray-400 mb-3" />
                <p className="text-gray-400 mb-1">Videos hierher ziehen oder klicken</p>
                <p className="text-xs text-gray-500">Unterstützte Formate: MP4, WEBM (max. 500MB)</p>
                
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept="video/*"
                  onChange={handleFileChange}
                />
                
                <Button variant="outline" className="mt-4" onClick={() => document.getElementById('file-upload')?.click()}>
                  Dateien auswählen
                </Button>
              </div>
              
              {uploadProgress !== null && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Uploading video...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full">
                    <div 
                      className="h-full bg-[#41c7f0] rounded-full transition-all duration-300" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
              
              <div className="mt-6">
                <h4 className="font-medium mb-3">Hochgeladene Video-Dateien</h4>
                
                {loading ? (
                  <p className="text-center py-4 text-gray-400">Lade Dateien...</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {files.map(file => (
                      <Card key={file.id} className="overflow-hidden bg-black/20 border-gray-700/70">
                        <div className="aspect-video bg-gray-900 relative">
                          {/* Display video thumbnail if available, otherwise a placeholder */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <FileVideo size={48} className="text-gray-600" />
                          </div>
                        </div>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <div className="truncate flex-1">
                              <p className="font-medium truncate" title={file.name}>
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-400">
                                {(file.size / (1024 * 1024)).toFixed(2)} MB
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="icon" 
                                className="h-8 w-8"
                                title="Als Lektion hinzufügen" 
                                onClick={() => {
                                  setSelectedFile(file);
                                  setShowAddLessonDialog(true);
                                }}
                              >
                                <Plus size={14} />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="icon" 
                                className="h-8 w-8 text-red-500 hover:text-red-400" 
                                onClick={() => handleDeleteFile(file.id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {files.length === 0 && !loading && (
                      <div className="col-span-full py-8 text-center text-gray-400">
                        <AlertCircle size={24} className="mx-auto mb-2" />
                        <p>Keine Video-Dateien gefunden</p>
                        <p className="text-sm mt-1">Laden Sie Videos hoch, um zu beginnen</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="courses">
            <div className="border border-gray-800 rounded-lg p-4 mb-4">
              <div className="flex justify-between mb-3">
                <h4 className="font-medium">Kurse</h4>
                <Button variant="outline" size="sm" onClick={() => setShowAddCourseDialog(true)}>
                  <Plus size={14} className="mr-1" /> Neuer Kurs
                </Button>
              </div>
              
              <ul className="divide-y divide-gray-800/30">
                <li className="py-3 flex justify-between items-center">
                  <span>Body Language Masterclass</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" className="h-8 w-8">
                      <Edit2 size={14} />
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8 text-red-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </li>
                <li className="py-3 flex justify-between items-center">
                  <span>Hoss Immo Vertriebsgenie Executive</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" className="h-8 w-8">
                      <Edit2 size={14} />
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8 text-red-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </li>
                <li className="py-3 flex justify-between items-center">
                  <span>Die Prinzipien der Skalierung</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" className="h-8 w-8">
                      <Edit2 size={14} />
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8 text-red-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </li>
              </ul>
            </div>
          </TabsContent>
          
          <TabsContent value="lessons">
            <div className="border border-gray-800 rounded-lg p-4">
              <div className="flex justify-between mb-3">
                <h4 className="font-medium">Lektionen</h4>
                <Button variant="outline" size="sm" onClick={() => setShowAddLessonDialog(true)}>
                  <Plus size={14} className="mr-1" /> Neue Lektion
                </Button>
              </div>
              
              <div className="mb-3">
                <label className="text-sm mb-1 block">Kurs auswählen</label>
                <select className="w-full bg-[#1a1a1a] border border-gray-800 rounded-md p-2 text-white">
                  <option value="1">Body Language Masterclass</option>
                  <option value="2">Hoss Immo Vertriebsgenie Executive</option>
                  <option value="3">Die Prinzipien der Skalierung</option>
                </select>
              </div>
              
              <ul className="divide-y divide-gray-800/30">
                <li className="py-3 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <FileVideo size={16} />
                    <span>Die Grundphilosophie der menschlichen Tonalität</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" className="h-8 w-8">
                      <Edit2 size={14} />
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8 text-red-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </li>
                <li className="py-3 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <FileVideo size={16} />
                    <span>Wie wirke ich kompetent?</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" className="h-8 w-8">
                      <Edit2 size={14} />
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8 text-red-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Dialog for adding a new course */}
      <Dialog open={showAddCourseDialog} onOpenChange={setShowAddCourseDialog}>
        <DialogContent className="sm:max-w-[500px] bg-slate-900 text-white">
          <DialogHeader>
            <DialogTitle>Neuen Kurs erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="course-title">Kursname</Label>
              <Input
                id="course-title"
                value={newCourse.title}
                onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
                placeholder="z.B. Body Language Masterclass"
              />
            </div>
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="course-instructor">Kursleiter</Label>
              <Input
                id="course-instructor"
                value={newCourse.instructor}
                onChange={(e) => setNewCourse({...newCourse, instructor: e.target.value})}
              />
            </div>
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="course-description">Beschreibung</Label>
              <Textarea
                id="course-description"
                value={newCourse.description}
                onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
                placeholder="Kursbeschreibung eingeben..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCourseDialog(false)}>Abbrechen</Button>
            <Button onClick={handleAddCourse}>Kurs speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog for adding a new lesson */}
      <Dialog open={showAddLessonDialog} onOpenChange={setShowAddLessonDialog}>
        <DialogContent className="sm:max-w-[500px] bg-slate-900 text-white">
          <DialogHeader>
            <DialogTitle>Neue Lektion erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="lesson-title">Titel der Lektion</Label>
              <Input
                id="lesson-title"
                value={newLesson.title}
                onChange={(e) => setNewLesson({...newLesson, title: e.target.value})}
                placeholder="z.B. Die Grundphilosophie der menschlichen Tonalität"
              />
            </div>
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="lesson-course">Kurs auswählen</Label>
              <Select 
                defaultValue={newLesson.courseId}
                onValueChange={(value) => setNewLesson({...newLesson, courseId: value})}
              >
                <SelectTrigger id="lesson-course">
                  <SelectValue placeholder="Wähle einen Kurs aus" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Body Language Masterclass</SelectItem>
                  <SelectItem value="2">Hoss Immo Vertriebsgenie Executive</SelectItem>
                  <SelectItem value="3">Die Prinzipien der Skalierung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid w-full items-center gap-2">
              <Label htmlFor="lesson-duration">Dauer (mm:ss)</Label>
              <Input
                id="lesson-duration"
                value={newLesson.duration}
                onChange={(e) => setNewLesson({...newLesson, duration: e.target.value})}
                placeholder="z.B. 10:30"
              />
            </div>
            
            <div className="grid w-full items-center gap-2">
              <Label>Video auswählen</Label>
              {selectedFile ? (
                <div className="bg-slate-800 p-3 rounded-md flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <FileVideo size={20} />
                    <span className="truncate max-w-[300px]">{selectedFile.name}</span>
                  </div>
                  <Check size={18} className="text-green-500" />
                </div>
              ) : (
                <div className="text-center p-6 border border-dashed border-gray-700 rounded-md">
                  <p className="text-gray-400">Bitte wählen Sie zuerst eine Video-Datei aus</p>
                  <Button 
                    variant="outline" 
                    className="mt-2"
                    onClick={() => setShowAddLessonDialog(false)}
                  >
                    Zurück zu Videos
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLessonDialog(false)}>Abbrechen</Button>
            <Button onClick={handleAddLesson} disabled={!selectedFile}>Lektion speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
